import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, User, Mail, FileText, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccessRequestPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestInfo, setRequestInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRequestInfo();
  }, [token]);

  const fetchRequestInfo = async () => {
    try {
      const response = await axios.get(`${API}/access-request/${token}`);
      setRequestInfo(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid access request link');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (approved) => {
    if (!user) {
      toast.error('Please log in to review this request');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/access-request/${token}/review`, null, {
        params: { approved }
      });
      
      toast.success(`Access ${approved ? 'approved' : 'denied'} successfully`);
      
      // Refresh the page to show updated status
      await fetchRequestInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to review request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-greek-blue to-blue-900">
        <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-greek-blue to-blue-900 p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Invalid Link</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-greek-blue hover:bg-blue-700">
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  const isReviewed = requestInfo.status !== 'pending';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-greek-blue to-blue-900 p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/1k2v2h81_AHEP_CA_LOGO.jfif" 
              alt="AHEPA Logo" 
              className="w-12 h-12 object-contain rounded-full"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/gl3qcnxw_DOP_LOGO_LARGE.jfif" 
              alt="DOP Logo" 
              className="w-12 h-12 object-contain rounded-full"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Scholarship Access Request</h1>
          <p className="text-slate-600">Review this applicant's request for scholarship access</p>
        </div>

        {/* Status Banner */}
        {isReviewed && (
          <div className={`rounded-lg p-4 mb-6 ${
            requestInfo.status === 'approved' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center gap-3">
              {requestInfo.status === 'approved' ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600" />
              )}
              <div>
                <p className={`font-medium ${
                  requestInfo.status === 'approved' ? 'text-green-800' : 'text-red-800'
                }`}>
                  Request {requestInfo.status === 'approved' ? 'Approved' : 'Denied'}
                </p>
                <p className={`text-sm ${
                  requestInfo.status === 'approved' ? 'text-green-600' : 'text-red-600'
                }`}>
                  Reviewed on {new Date(requestInfo.reviewed_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Scholarship Info */}
        <div className="bg-greek-blue/5 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-slate-900 mb-2">{requestInfo.scholarship_name}</h3>
          <p className="text-sm text-slate-600">
            Request submitted on {new Date(requestInfo.created_at).toLocaleDateString()}
          </p>
        </div>

        {/* Applicant Info */}
        <div className="border border-slate-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
            <User className="w-4 h-4" />
            Applicant Information
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-greek-blue/10 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-greek-blue" />
              </div>
              <div>
                <p className="font-medium text-slate-900">{requestInfo.user_name || 'Unknown'}</p>
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {requestInfo.user_email}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Eligibility Answers */}
        {requestInfo.eligibility_answers && Object.keys(requestInfo.eligibility_answers).length > 0 && (
          <div className="border border-slate-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Eligibility Information
            </h3>
            
            <div className="space-y-3">
              {Object.entries(requestInfo.eligibility_answers).map(([key, value]) => (
                <div key={key} className="border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                  <p className="text-sm font-medium text-slate-700 capitalize">
                    {key.replace(/_/g, ' ')}
                  </p>
                  <p className="text-slate-900">
                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value || 'N/A'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!isReviewed && (
          <>
            {!user && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-800 font-medium">Login Required</p>
                    <p className="text-sm text-yellow-700">
                      Please <a href="/admin/login" className="underline font-medium">log in</a> as a committee chair to review this request.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-4">
              <Button
                onClick={() => handleReview(false)}
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                disabled={submitting || !user}
                data-testid="deny-access-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                Deny Access
              </Button>
              
              <Button
                onClick={() => handleReview(true)}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={submitting || !user}
                data-testid="approve-access-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Approve Access
              </Button>
            </div>
          </>
        )}

        {isReviewed && (
          <Button
            onClick={() => navigate('/committee-dashboard')}
            className="w-full bg-greek-blue hover:bg-blue-700"
          >
            Return to Dashboard
          </Button>
        )}
      </div>
    </div>
  );
};

export default AccessRequestPage;
